import UIComponent from "../ui/component";
import accountTabsApi from "./AccountTabsApi";

export class AccountTabs extends UIComponent {
    activeRequestId = 0;
    state = {
        tab: "invoices",
        invoices_page: 1,
        transactions_page: 1,
        invoice_id: null,
    };

    init() {
        this.includeComponents({
            invoicesRenderer: "document@#accountInvoicesRenderer",
            transactionsRenderer: "document@#accountTransactionsRenderer",
        });

        accountTabsApi.setEndpoint(this.tabs_url);
        this.state = this.readStateFromUrl();
        this.state.tab = this.normalizeTab(this.state.tab);

        this.bindTabClicks();
        this.bindPaginationClicks();
        window.addEventListener("popstate", this.handlePopState);

        this.setTabUiState(this.state.tab);
    }

    destroy() {
        window.removeEventListener("popstate", this.handlePopState);
    }

    handlePopState = () => {
        this.state = this.readStateFromUrl();
        this.state.tab = this.normalizeTab(this.state.tab);
        this.setTabUiState(this.state.tab);
        this.fetchAndRender(this.state.tab, false);
    };

    bindTabClicks() {
        const tabLinks = this.findAll('[data-tbx-name="tabLink"]');
        tabLinks.forEach((tabLink) => {
            tabLink.addEventListener("click", (event) => {
                event.preventDefault();
                const targetTab = tabLink.dataset.targetTab;
                if (!targetTab || targetTab === this.state.tab) {
                    return;
                }

                this.state.tab = targetTab;
                this.setTabUiState(targetTab);
                this.pushStateToUrl();

                if (targetTab === "invoices" || targetTab === "transactions") {
                    this.fetchAndRender(targetTab, false);
                }
            });
        });
    }

    bindPaginationClicks() {
        this.element.addEventListener("click", (event) => {
            const link = event.target.closest(
                '[data-tbx-name="invoicesPagination"] a.page-link, [data-tbx-name="transactionsPagination"] a.page-link'
            );
            if (!link) {
                return;
            }

            event.preventDefault();

            const isInvoices = !!link.closest('[data-tbx-name="invoicesPagination"]');
            const targetTab = isInvoices ? "invoices" : "transactions";
            const pageParam = isInvoices ? "invoices_page" : "transactions_page";
            const page = this.resolvePageFromPaginationLink(link, pageParam);

            if (!Number.isFinite(page) || page < 1) {
                return;
            }

            this.state.tab = targetTab;
            this.state[pageParam] = page;
            this.setTabUiState(targetTab);
            this.pushStateToUrl();
            this.fetchAndRender(targetTab, false);
        });
    }

    resolvePageFromPaginationLink(link, pageParam) {
        const datasetPage = Number(link.dataset.page);
        if (Number.isFinite(datasetPage) && datasetPage > 0) {
            return datasetPage;
        }

        const href = link.getAttribute("href");
        if (!href || href === "#") {
            return NaN;
        }

        try {
            const url = new URL(href, window.location.origin);
            const hrefPage = Number(url.searchParams.get(pageParam));
            return Number.isFinite(hrefPage) && hrefPage > 0 ? hrefPage : 1;
        } catch (error) {
            return NaN;
        }
    }

    async fetchAndRender(tab, pushState = true) {
        const requestId = ++this.activeRequestId;

        try {
            const payload = await accountTabsApi.fetchState(this.state);
            if (requestId !== this.activeRequestId) {
                return;
            }

            this.state.tab = this.normalizeTab(payload.tab || this.state.tab);

            if (tab === "invoices") {
                this.renderInvoices(payload.invoices);
            } else if (tab === "transactions") {
                this.renderTransactions(payload.transactions);
            }

            this.setTabUiState(this.state.tab);
            if (pushState) {
                this.pushStateToUrl();
            }
        } catch (error) {
            console.error("Failed to fetch account tabs data", error);
        }
    }

    renderInvoices(invoicesPayload = {}) {
        if (!this.invoicesRenderer) {
            return;
        }

        this.invoicesRenderer.render(invoicesPayload.data, invoicesPayload);
    }

    renderTransactions(transactionsPayload = {}) {
        if (!this.transactionsRenderer) {
            return;
        }

        this.transactionsRenderer.render(transactionsPayload.data, transactionsPayload);
    }

    setTabUiState(tab) {
        this.findAll('[data-tbx-name="tabLink"]').forEach((link) => {
            const isActive = link.dataset.targetTab === tab;
            link.classList.toggle("active", isActive);
        });

        this.findAll('[data-tbx-name="tabPane"]').forEach((pane) => {
            const isActive = pane.dataset.tab === tab;
            pane.classList.toggle("active", isActive);
            pane.classList.toggle("show", isActive);
        });
    }

    readStateFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get("tab") || this.getInitialTabFromDom();

        return {
            tab,
            invoices_page: Number(params.get("invoices_page") || 1),
            transactions_page: Number(params.get("transactions_page") || 1),
            invoice_id: params.get("invoice_id"),
        };
    }

    getInitialTabFromDom() {
        const activeTabLink = this.find('[data-tbx-name="tabLink"].active');
        const activeTab = activeTabLink?.dataset?.targetTab;
        return activeTab || "invoices";
    }

    normalizeTab(tab) {
        const allowedTabs = ["invoices", "transactions", "subscriptions"];
        if (!allowedTabs.includes(tab)) {
            return "invoices";
        }

        return tab;
    }

    pushStateToUrl() {
        const url = new URL(window.location.href);
        url.searchParams.set("tab", this.state.tab);

        if (this.state.invoices_page > 1) {
            url.searchParams.set("invoices_page", this.state.invoices_page);
        } else {
            url.searchParams.delete("invoices_page");
        }

        if (this.state.transactions_page > 1) {
            url.searchParams.set("transactions_page", this.state.transactions_page);
        } else {
            url.searchParams.delete("transactions_page");
        }

        if (this.state.invoice_id) {
            url.searchParams.set("invoice_id", this.state.invoice_id);
        } else {
            url.searchParams.delete("invoice_id");
        }

        window.history.pushState({}, "", url.toString());
    }
}

